// components/workspace-timeline/WorkspaceTimelineInsertLanePreview.tsx
import React, { useMemo } from 'react';
import { clamp, timelineDropPreviewFill } from './presentation';
import type { TimelineFileDropPreview, TrackLaneDragPreview } from './types';

interface WorkspaceTimelineInsertLanePreviewProps {
  shouldRender: boolean;
  shouldShowFileDropPreview: boolean;
  insertEndLaneRef: React.RefObject<HTMLDivElement | null>;
  laneLeftColumnWidthPx: number;
  timelineFileDropPreview: TimelineFileDropPreview | null;
  trackLaneDragPreview: TrackLaneDragPreview | null;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeMs: number;
  timelineDropPlaceholderDurationMs: number;
}

const TIMELINE_INSERT_LANE_SHELL_STYLE: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px dashed rgba(236,236,236,0.25)',
  backgroundColor: 'rgba(255,255,255,0.02)',
};
const TIMELINE_INSERT_LANE_BARE_SHELL_STYLE: React.CSSProperties = {
  borderRadius: '9999px',
  border: '1px dashed rgba(236,236,236,0.25)',
  backgroundColor: 'rgba(255,255,255,0.02)',
};
const TIMELINE_INSERT_LANE_HINT_STYLE: React.CSSProperties = { fontSize: 13, color: '#8E8E8E' };
const TIMELINE_INSERT_LANE_FILE_DROP_PREVIEW_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '1px',
  bottom: '1px',
  borderRadius: '6px',
  border: '1px dashed rgba(236,236,236,0.45)',
  opacity: 0.88,
};
const TIMELINE_INSERT_LANE_TRACK_DRAG_PREVIEW_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '1px',
  bottom: '1px',
  borderRadius: '6px',
  border: '1px dashed rgba(236,236,236,0.55)',
  backgroundColor: 'rgba(236,236,236,0.18)',
  opacity: 0.9,
};

export const WorkspaceTimelineInsertLanePreview = React.memo(({
  shouldRender,
  shouldShowFileDropPreview,
  insertEndLaneRef,
  laneLeftColumnWidthPx,
  timelineFileDropPreview,
  trackLaneDragPreview,
  timelineRenderRangeStartMs,
  timelineRenderRangeMs,
  timelineDropPlaceholderDurationMs,
}: WorkspaceTimelineInsertLanePreviewProps) => {
  // No name column (the empty timeline): the lane alone, as tall as the 32px button beside it.
  const isBare = laneLeftColumnWidthPx === 0;
  const rootStyle = useMemo<React.CSSProperties>(() => ({
    gridTemplateColumns: isBare ? 'minmax(0,1fr)' : `${laneLeftColumnWidthPx}px minmax(0,1fr)`,
  }), [isBare, laneLeftColumnWidthPx]);
  const fileDropPreviewStyle = useMemo<React.CSSProperties | null>(() => {
    if (!shouldShowFileDropPreview || !timelineFileDropPreview) return null;
    const startPercent = clamp(
      ((timelineFileDropPreview.startMs - timelineRenderRangeStartMs) / Math.max(1, timelineRenderRangeMs)) * 100,
      0,
      100
    );
    const widthPercent = clamp(
      (timelineDropPlaceholderDurationMs / Math.max(1, timelineRenderRangeMs)) * 100,
      0.6,
      100
    );
    const backgroundColor = timelineDropPreviewFill(timelineFileDropPreview.layerType);
    return {
      ...TIMELINE_INSERT_LANE_FILE_DROP_PREVIEW_BASE_STYLE,
      left: `${startPercent}%`,
      width: `${widthPercent}%`,
      backgroundColor,
    };
  }, [
    shouldShowFileDropPreview,
    timelineDropPlaceholderDurationMs,
    timelineFileDropPreview,
    timelineRenderRangeMs,
    timelineRenderRangeStartMs,
  ]);
  const trackDragPreviewStyle = useMemo<React.CSSProperties | null>(() => {
    if (trackLaneDragPreview?.mode !== 'insert' || !trackLaneDragPreview.insertAtEnd) return null;
    const startPercent = clamp(
      ((trackLaneDragPreview.startMs - timelineRenderRangeStartMs) / Math.max(1, timelineRenderRangeMs)) * 100,
      0,
      100
    );
    const widthPercent = clamp(
      (trackLaneDragPreview.durationMs / Math.max(1, timelineRenderRangeMs)) * 100,
      0.6,
      100
    );
    return {
      ...TIMELINE_INSERT_LANE_TRACK_DRAG_PREVIEW_BASE_STYLE,
      left: `${startPercent}%`,
      width: `${widthPercent}%`,
    };
  }, [timelineRenderRangeMs, timelineRenderRangeStartMs, trackLaneDragPreview]);

  if (!shouldRender) return null;
  return (
    <div
      ref={insertEndLaneRef}
      className="grid w-full items-center gap-2"
      style={rootStyle}
    >
      {!isBare && <div />}
      <div className={`relative flex ${isBare ? 'h-8' : 'h-7'} flex-1 items-center justify-center overflow-hidden`} style={isBare ? TIMELINE_INSERT_LANE_BARE_SHELL_STYLE : TIMELINE_INSERT_LANE_SHELL_STYLE}>
        {fileDropPreviewStyle === null && trackDragPreviewStyle === null && (
          <span className="pointer-events-none" style={TIMELINE_INSERT_LANE_HINT_STYLE}>Drop video, audio or images here</span>
        )}
        {fileDropPreviewStyle !== null && <div className="pointer-events-none" style={fileDropPreviewStyle} />}
        {trackDragPreviewStyle !== null && <div className="pointer-events-none" style={trackDragPreviewStyle} />}
      </div>
    </div>
  );
});

WorkspaceTimelineInsertLanePreview.displayName = 'WorkspaceTimelineInsertLanePreview';
